/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/no-misused-promises */
import { createFilterOptions } from "@mui/material/useAutocomplete";
import {
  SmartAutoComplete,
  SmartInputBaseProps,
  usePopup,
} from "@nodeon/react-components";
import React, { useEffect, useState } from "react";
import {
  FieldPathValue,
  FieldValues,
  Path,
  UnpackNestedValue,
  UseFormReturn,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useCoordinateSystemApi } from "~/api/GeneratedApi";
import CoordinateSystemDialog from "~/pages/marine-survey-and-design-page/dialogs/CoordinateSystemDialog";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";

interface Props<FormT extends FieldValues> {
  readOnly?: boolean;
  name: Path<FormT>;
  formMethods: UseFormReturn<FormT>;
  gridProps?: SmartInputBaseProps<FormT>["gridProps"];
  required?: boolean;
  onChange?: (value: string) => void;
}

const filter = createFilterOptions<number>();

const CoordinateSystemAutocomplete = <FormT extends FieldValues>({
  formMethods,
  readOnly,
  name,
  gridProps,
  required,
  onChange,
}: Props<FormT>): JSX.Element | null => {
  const { t } = useTranslation("coordinate-system");
  const { show } = usePopup();
  const { getCoordinateSystems } = useCoordinateSystemApi();
  const [inputValue, setInputValue] = useState("");
  const [hasValue, setHasValue] = useState(false);
  const { setValue, watch } = formMethods;
  const watchVal = watch(name);

  React.useEffect(() => {
    if (onChange) onChange(inputValue);
  }, [inputValue, onChange]);

  useEffect(() => {
    setHasValue(watchVal !== null && watchVal !== undefined);
  }, [watchVal]);

  const { data, isLoading, refetch } = useCustomQuery(
    "coordinate-systems",
    () => getCoordinateSystems(),
    {
      onSuccess: (d) => {
        const val = d.results.find((et) => et.name === inputValue);
        if (val) {
          setValue(
            name,
            val.id as UnpackNestedValue<FieldPathValue<FormT, typeof name>>,
          );
        }
      },
    },
  );

  return isLoading ? null : (
    <SmartAutoComplete<FormT, number, undefined, undefined, true>
      name={name}
      controllerProps={{
        rules: {
          required: required
            ? { value: true, message: t("form:required") }
            : false,
        },
      }}
      label={t("label")}
      gridProps={gridProps}
      fullWidth
      options={data?.results.map((e) => e.id) ?? []}
      freeSolo
      getOptionLabel={(opt) => {
        if (opt >= 0) {
          const d = data?.results.find((x) => x.id === opt);
          return d
            ? `EPSG:${d.name}${d.description ? ` - ${d?.description}` : ""}`
            : t("general:unknown");
        }
        if (opt === Number.NEGATIVE_INFINITY) {
          return !inputValue
            ? `(${t("general:add-new")})`
            : `(${t("general:add")} EPSG:${inputValue})`;
        }
        return "";
      }}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);
        if (!hasValue) {
          filtered.push(Number.NEGATIVE_INFINITY);
        }

        return filtered;
      }}
      checkAdd={(val) => !data?.results.find((x) => x.id === val)}
      onAdd={() =>
        show(
          <CoordinateSystemDialog
            onSubmitted={(val) => {
              setInputValue(val);
              refetch();
            }}
            initialValue={inputValue}
          />,
          { noDialog: true },
        )
      }
      onInputChange={(_, v) => setInputValue(v)}
      disabled={readOnly}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
    />
  );
};

export default CoordinateSystemAutocomplete;
